import { type RefObject, useRef } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { BlankSlate } from "../blank-slate";
import { CalendarControls } from "./calendar-controls";
import { CalendarDayColumn } from "./calendar-day-column";
import { CalendarScroller } from "./calendar-scroller";
import { CalendarContext } from "./context";
import type { CalendarDate, CalendarItem, CalendarItemColor } from "./shared";
import { useCalendarControls } from "./use-calendar-controls";

/** Props Interface */
interface CalendarProps<T extends object> {
	data: Array<CalendarDate<T>>;
	initialDate: string;
	onStartReached?: () => void;
	onEndReached?: () => void;

	getItemColor?: (item: CalendarItem<T>) => CalendarItemColor;
	renderItemContent?: (item: CalendarItem<T>) => React.ReactNode;
	onItemClick?: (item: CalendarItem<T>) => void;

	virtuosoHandle?: RefObject<VirtuosoHandle>;
}

function Calendar<T extends object>({
	data,
	initialDate,
	onEndReached,
	onStartReached,
	getItemColor,
	renderItemContent,
	onItemClick,
	virtuosoHandle: outerVirtuosoHandle,
}: CalendarProps<T>) {
	/** Computed */
	const startIndex = data.findIndex((item) => item.date === initialDate);
	const earliestDate = data.at(0)?.date;

	const innerVirtuosoHandle = useRef<VirtuosoHandle>(null) as RefObject<VirtuosoHandle>;
	const virtuosoHandle = outerVirtuosoHandle || innerVirtuosoHandle;

	if (!earliestDate)
		return (
			<div className="w-full h-full card">
				<BlankSlate />
			</div>
		);

	/** Render */
	return (
		<CalendarContext.Provider
			value={{
				earliestDate,
				virtuosoHandle,
				getItemColor: (item) => {
					return getItemColor?.(item as any);
				},
				renderItemContent: (item) => {
					return renderItemContent?.(item as any);
				},
				onItemClick: (item) => {
					onItemClick?.(item as any);
				},
			}}
		>
			<div className="w-full h-full card p-0 overflow-hidden relative">
				<Virtuoso
					ref={virtuosoHandle}
					className="p-0 snap-x snap-proximity"
					style={{
						width: "100%",
						minHeight: "100%",
						overflowX: "scroll",
					}}
					data={data}
					horizontalDirection
					initialTopMostItemIndex={startIndex}
					startReached={onStartReached}
					endReached={onEndReached}
					itemContent={(index, item) => (
						<CalendarDayColumn
							index={index}
							item={item}
						/>
					)}
					components={{
						Scroller: CalendarScroller,
					}}
				/>
			</div>
		</CalendarContext.Provider>
	);
}

Calendar.Controls = CalendarControls;
Calendar.useControls = useCalendarControls;

export { Calendar };
